import { useEffect, useRef, useState, useCallback } from 'react';

const DRAFT_PREFIX = 'gp_draft_';
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
const DEBOUNCE_MS = 400;

interface StoredDraft<T> {
  data: T;
  timestamp: number;
}

export function useFormDraft<T extends Record<string, unknown>>(
  key: string,
  state: T,
  isOpen: boolean,
  enabled = true
): {
  restoreIfPresent: () => T | null;
  clearDraft: () => void;
  hasDraft: boolean;
} {
  const storageKey = `${DRAFT_PREFIX}${key}`;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [hasDraft, setHasDraft] = useState(false);

  // Debounced write when modal is open and enabled
  useEffect(() => {
    if (!isOpen || !enabled) return;

    timerRef.current = setTimeout(() => {
      try {
        const stored: StoredDraft<T> = { data: state, timestamp: Date.now() };
        sessionStorage.setItem(storageKey, JSON.stringify(stored));
      } catch {
        // Private browsing or quota exceeded — fail silently
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [isOpen, enabled, state, storageKey]);

  // Clear timer when modal closes
  useEffect(() => {
    if (!isOpen) {
      clearTimeout(timerRef.current);
    }
  }, [isOpen]);

  const restoreIfPresent = useCallback((): T | null => {
    if (!enabled) return null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const stored: StoredDraft<T> = JSON.parse(raw);
      if (Date.now() - stored.timestamp > DRAFT_MAX_AGE_MS) {
        sessionStorage.removeItem(storageKey);
        return null;
      }
      setHasDraft(true);
      return stored.data;
    } catch {
      return null;
    }
  }, [enabled, storageKey]);

  const clearDraft = useCallback(() => {
    clearTimeout(timerRef.current);
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // fail silently
    }
    setHasDraft(false);
  }, [storageKey]);

  return { restoreIfPresent, clearDraft, hasDraft };
}
